import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { Box, Typography } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { DistaAutocomplete } from "@sista/dista-ui";
import { useTranslation } from "react-i18next";
import { loadUseSistaChipDataGetter } from "@sista/sista-chip";
import { IdmClientContext } from "@sista/idm-client";
import { SistaId } from "@sista/library-stasi";
export const useSistaChipDataGetter = await loadUseSistaChipDataGetter();
export const applyVariantUrns = (base, urnsData) => {
    const { providerUrns, programUrns, cfpUrns } = urnsData;
    if (providerUrns.length > 1 ||
        programUrns.length > 1 ||
        cfpUrns.length > 1) {
        return {
            ...base,
            providerUrn: null,
            programUrn: null,
            cfpUrn: null,
            providerUrns,
            programUrns,
            cfpUrns,
        };
    }
    return {
        ...base,
        providerUrn: providerUrns[0] ?? null,
        programUrn: programUrns[0] ?? null,
        cfpUrn: cfpUrns[0] ?? null,
        providerUrns: null,
        programUrns: null,
        cfpUrns: null,
    };
};
export const isMultiEntityVariant = (text) => {
    if (text == null)
        return false;
    const providerLen = text.providerUrns?.length ?? 0;
    const programLen = text.programUrns?.length ?? 0;
    const cfpLen = text.cfpUrns?.length ?? 0;
    return providerLen > 1 || programLen > 1 || cfpLen > 1;
};
const OptionAutocomplete = DistaAutocomplete;
const defaultFilterOptions = createFilterOptions();
export const SelectVariantUrns = ({ canEdit, data, tree, stacked, onChange, }) => {
    const { t } = useTranslation("sprava-textu");
    const chipDataGetter = useSistaChipDataGetter();
    const { loggedAccount } = useContext(IdmClientContext);
    const providerUrns = data?.providerUrns ?? [];
    const programUrns = data?.programUrns ?? [];
    const cfpUrns = data?.cfpUrns ?? [];
    const getChipLabel = useCallback(async (urn) => {
        try {
            const chip = await chipDataGetter(urn, loggedAccount.authKey);
            return chip ? chip.displayName : urn;
        }
        catch (error) {
            return urn;
        }
    }, [chipDataGetter, loggedAccount.authKey]);
    const [providerUrnOptions, setProviderUrnOptions] = useState(null);
    const [programUrnOptions, setProgramUrnOptions] = useState(null);
    const [cfpUrnOptions, setCfpUrnOptions] = useState(null);
    useEffect(() => {
        if (!tree)
            return;
        const loadProviderOptions = async () => {
            const options = await Promise.all(tree.map(async (a) => ({
                value: a.urn,
                label: await getChipLabel(a.urn),
            })));
            setProviderUrnOptions(options);
        };
        loadProviderOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree]);
    useEffect(() => {
        if (!tree || providerUrns.length === 0) {
            setProgramUrnOptions([]);
            return;
        }
        const loadProgramOptions = async () => {
            const programs = tree
                .filter((p) => providerUrns.includes(p.urn))
                .flatMap((p) => p.programs ?? []);
            const options = await Promise.all(programs.map(async (p) => ({
                value: p.urn,
                label: await getChipLabel(p.urn),
            })));
            setProgramUrnOptions(options);
        };
        loadProgramOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, providerUrns.join(",")]);
    useEffect(() => {
        if (!tree || programUrns.length === 0) {
            setCfpUrnOptions([]);
            return;
        }
        const loadCfpOptions = async () => {
            const cfps = tree
                .flatMap((p) => p.programs ?? [])
                .filter((p) => programUrns.includes(p.urn))
                .flatMap((p) => p.cfps ?? []);
            const options = await Promise.all(cfps.map(async (c) => ({
                value: c,
                label: await getChipLabel(c),
            })));
            setCfpUrnOptions(options);
        };
        loadCfpOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree, programUrns.join(",")]);
    const providerOptionsFiltered = useMemo(() => {
        if (loggedAccount.providerId === "tacr")
            return providerUrnOptions ?? [];
        return (providerUrnOptions ?? []).filter((opt) => opt.value ===
            new SistaId("providers", loggedAccount.providerId).asUrn());
    }, [providerUrnOptions, loggedAccount.providerId]);
    const toDisplayValue = (urns, options) => urns.map((urn) => options?.find((o) => o.value === urn) ?? { value: urn, label: urn });
    const filterOptionsWithCustom = useCallback((options, params) => {
        const filtered = defaultFilterOptions(options, params);
        const inputValue = params.inputValue.trim();
        const isExisting = options.some((option) => option.value === inputValue || option.label === inputValue);
        if (inputValue !== "" && !isExisting) {
            filtered.push({
                value: inputValue,
                label: t("sprava-textu.selectVariantUrns.addCustomOption", 'Přidat "{{value}}" jako vlastní URN', { value: inputValue }),
                custom: true,
            });
        }
        return filtered;
    }, [t]);
    return (_jsxs(Box, { sx: {
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
            alignItems: "flex-start",
        }, children: [_jsx(Box, { sx: { flex: stacked ? "1 1 100%" : "1 1 15em" }, children: _jsx(OptionAutocomplete, { multiple: true, label: t("sprava-textu.selectVariantUrns.label.providerUrn", "Poskytovatel"), options: providerOptionsFiltered, renderOption: (props, option) => (_createElement("li", { ...props, key: option.value }, option.label)), filterOptions: filterOptionsWithCustom, selectOnFocus: true, clearOnBlur: true, handleHomeEndKeys: true, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, value: toDisplayValue(providerUrns, providerUrnOptions), onChange: (_e, newValue) => {
                        onChange({
                            providerUrns: newValue.map((v) => v.value),
                            programUrns: [],
                            cfpUrns: [],
                        });
                    }, helperText: providerUrns.length === 0 ? (_jsx(Typography, { color: "error", children: t("sprava-textu.selectVariantUrns.providerUrnNeeded", "Tato položka je povinná.") })) : null, loading: providerUrnOptions == null, size: "medium", sx: { minWidth: "15em", maxWidth: "20em" }, disabled: !canEdit }) }), _jsx(Box, { sx: { flex: stacked ? "1 1 100%" : "1 1 15em" }, children: _jsx(OptionAutocomplete, { multiple: true, label: t("sprava-textu.selectVariantUrns.label.programUrn", "Program"), options: programUrnOptions ?? [], renderOption: (props, option) => (_createElement("li", { ...props, key: option.value }, option.label)), filterOptions: filterOptionsWithCustom, selectOnFocus: true, clearOnBlur: true, handleHomeEndKeys: true, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, value: toDisplayValue(programUrns, programUrnOptions), onChange: (_e, newValue) => {
                        onChange({
                            providerUrns,
                            programUrns: newValue.map((v) => v.value),
                            cfpUrns: [],
                        });
                    }, loading: programUrnOptions == null, size: "medium", sx: { minWidth: "15em", maxWidth: "20em" }, disabled: !canEdit || providerUrns.length === 0 }) }), _jsx(Box, { sx: { flex: stacked ? "1 1 100%" : "1 1 15em" }, children: _jsx(OptionAutocomplete, { multiple: true, label: t("sprava-textu.selectVariantUrns.label.cfpUrn", "Soutěž"), options: cfpUrnOptions ?? [], renderOption: (props, option) => (_createElement("li", { ...props, key: option.value }, option.label)), filterOptions: filterOptionsWithCustom, selectOnFocus: true, clearOnBlur: true, handleHomeEndKeys: true, getOptionLabel: (option) => option.label, isOptionEqualToValue: (option, value) => option.value === value.value, value: toDisplayValue(cfpUrns, cfpUrnOptions), onChange: (_e, newValue) => {
                        onChange({
                            providerUrns,
                            programUrns,
                            cfpUrns: newValue.map((v) => v.value),
                        });
                    }, loading: cfpUrnOptions == null, size: "medium", sx: { minWidth: "15em", maxWidth: "20em" }, disabled: !canEdit || programUrns.length === 0 }) })] }));
};
